import moment, { type Moment } from 'moment-timezone';

/**
 * Merges overlapping or adjacent availability slots into a minimal set of non-overlapping slots.
 * @param slots - Array of availability slots with start/end ISO strings.
 * @param timezone - Target timezone for accurate time comparisons (e.g., "America/New_York").
 * @returns Array of merged, non-overlapping slots as { start, end } objects with ISO strings.
 */
const mergeOverlappingSlots = (
  slots: { start: string; end: string }[],
  timezone: string
): { start: string; end: string }[] => {
  // If there are 0 or 1 slots, no merging is needed
  if (slots.length <= 1) return slots;

  // Convert ISO strings to moment objects in the target timezone and validate
  const momentSlots = slots
    .map((slot) => ({
      start: moment(slot.start).tz(timezone),
      end: moment(slot.end).tz(timezone),
    }))
    // Filter out invalid slots (invalid dates or end <= start)
    .filter(
      (slot) =>
        slot.start.isValid() &&
        slot.end.isValid() &&
        slot.start.isBefore(slot.end)
    )
    // Sort by start time for chronological processing
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  // If all slots were invalid, return empty array
  if (momentSlots.length === 0) return [];

  // Initialize merged array with the first slot
  const merged: { start: Moment; end: Moment }[] = [momentSlots[0]];

  // Iterate through remaining slots to merge overlaps or adjacent slots
  for (let i = 1; i < momentSlots.length; i++) {
    const current = momentSlots[i];
    const lastMerged = merged[merged.length - 1];

    // Check if current slot overlaps or is adjacent to the last merged slot
    if (current.start.isSameOrBefore(lastMerged.end)) {
      // Extend the end time of lastMerged if current slot ends later
      if (current.end.isAfter(lastMerged.end)) {
        lastMerged.end = current.end;
      }
      // If current slot is fully contained, no action needed
    } else {
      // No overlap: add current slot as a new entry
      merged.push(current);
    }
  }

  // Convert merged moment objects back to ISO strings
  return merged.map((slot) => ({
    start: slot.start.toISOString(),
    end: slot.end.toISOString(),
  }));
};

/**
 * Computes the intersection of availability slots across multiple users for a single day.
 * Uses a sweep-line algorithm to find periods where at least minRequiredUsers are available.
 * @param userSlots - Array of slot arrays, one per user, with start/end ISO strings.
 * @param timezone - Target timezone for time comparisons.
 * @param minRequiredUsers - Minimum number of users required for a valid intersection.
 * @returns Array of common availability slots as { start, end } objects with ISO strings.
 */
const computeSlotIntersection = (
  userSlots: { start: string; end: string }[][],
  timezone: string,
  minRequiredUsers: number
): { start: string; end: string }[] => {
  // If too few users or all users have no slots, return empty array
  if (
    userSlots.length < minRequiredUsers ||
    userSlots.every((slots) => slots.length === 0)
  ) {
    return [];
  }

  // Create events for the sweep-line algorithm: one for each slot's start and end
  const events: { time: Moment; isStart: boolean; userIndex: number }[] = [];
  userSlots.forEach((slots, userIndex) => {
    slots.forEach((slot) => {
      const start = moment(slot.start).tz(timezone);
      const end = moment(slot.end).tz(timezone);
      // Only include valid slots
      if (start.isValid() && end.isValid() && start.isBefore(end)) {
        events.push({ time: start, isStart: true, userIndex });
        events.push({ time: end, isStart: false, userIndex });
      }
    });
  });

  // If no valid events, return empty array
  if (events.length === 0) return [];

  // Sort events by time; if times are equal, process end events before start events
  events.sort((a, b) => {
    const timeDiff = a.time.valueOf() - b.time.valueOf();
    if (timeDiff !== 0) return timeDiff;
    return a.isStart ? 1 : -1; // End events first to handle exact overlaps
  });

  // Track active users and common slots
  const commonSlots: { start: string; end: string }[] = [];
  const activeUsers = new Set<number>();
  let lastStart: Moment | null = null;

  // Process events to find periods with enough overlapping users
  for (const event of events) {
    if (event.isStart) {
      // Add user to active set
      activeUsers.add(event.userIndex);
      // If we now have enough users and no active slot, start a new slot
      if (activeUsers.size >= minRequiredUsers && lastStart === null) {
        lastStart = event.time;
      }
    } else {
      // If we had enough users, close the current slot
      if (activeUsers.size >= minRequiredUsers && lastStart !== null) {
        commonSlots.push({
          start: lastStart.toISOString(),
          end: event.time.toISOString(),
        });
      }
      // Remove user from active set
      activeUsers.delete(event.userIndex);
      // Update lastStart based on remaining active users
      lastStart = activeUsers.size >= minRequiredUsers ? event.time : null;
    }
  }

  // Merge any adjacent or overlapping common slots
  return mergeOverlappingSlots(commonSlots, timezone);
};

/**
 * Processes raw availability data for multiple users across time ranges (e.g., weeks).
 * Computes union (one-on-one) or intersection (panel) of availability slots.
 * @param input - Raw availability data keyed by time range and user ID, with UTC ISO strings.
 * @param calendarApi - Optional FullCalendar API to get the current timezone.
 * @param isOneOnOne - If true, computes union of availability; if false, computes intersection.
 * @returns Object keyed by time range, containing arrays of merged availability slots.
 */
export function SchProcessAvailability({
  input,
  timezone,
  isOneOnOne = false,
  userMeetingRooms = [],
}: {
  input: Record<
    string,
    Record<string, { availability?: { start: string; end: string }[] }>
  >;
  timezone: string;
  isOneOnOne?: boolean;
  userMeetingRooms?: string[];
}): Record<string, { start: string; end: string }[]> {
  // Initialize output object to store results
  const output: Record<string, { start: string; end: string }[]> = {};

  // Validate input
  if (!input || typeof input !== 'object') {
    console.warn('SchProcessAvailability: Invalid input:', input);
    return output;
  }

  // Process each time range (e.g., week) in the input
  for (const timeRange in input) {
    // Skip inherited properties
    if (!Object.prototype.hasOwnProperty.call(input, timeRange)) continue;

    const usersData = input[timeRange];
    // Validate user data for this time range
    if (!usersData || typeof usersData !== 'object') {
      console.warn(`Skipping time range "${timeRange}": Invalid user data`);
      continue;
    }

    const userIds = Object.keys(usersData);
    const totalUsers = userIds.length;
    // If no users, assign empty availability
    if (totalUsers === 0) {
      output[timeRange] = [];
      continue;
    }

    // Determine minimum required users: 1 for one-on-one, all or specified for panel
    const requiredUsers = isOneOnOne
      ? 1
      : totalUsers - (userMeetingRooms?.length || 0);

    // Map to store slots by date and user for efficient processing
    const daysMap = new Map<
      string,
      Map<string, { start: string; end: string }[]>
    >();

    // Step 1: Populate daysMap with slots, splitting multi-day slots across dates
    for (const userId of userIds) {
      const userData = usersData[userId];
      // Skip if user data or availability is missing/invalid
      if (!userData || !Array.isArray(userData.availability)) continue;

      for (const slot of userData.availability) {
        // Validate slot structure
        if (
          !slot ||
          typeof slot.start !== 'string' ||
          typeof slot.end !== 'string'
        ) {
          console.warn(
            `Skipping invalid slot for user ${userId} in ${timeRange}:`,
            slot
          );
          continue;
        }

        // Parse UTC ISO strings and convert to target timezone
        const startMoment = moment.utc(slot.start).tz(timezone);
        const endMoment = moment.utc(slot.end).tz(timezone);

        // Skip invalid or malformed slots
        if (
          !startMoment.isValid() ||
          !endMoment.isValid() ||
          startMoment.isSameOrAfter(endMoment)
        ) {
          console.warn(
            `Skipping invalid slot for user ${userId} in ${timeRange}:`,
            slot
          );
          continue;
        }

        // Iterate through all calendar days the slot spans in the target timezone
        const current = startMoment.clone().startOf('day');
        const endDay = endMoment.clone().startOf('day');

        while (current.isSameOrBefore(endDay, 'day')) {
          const date = current.format('YYYY-MM-DD');
          // Initialize date entry if not present
          if (!daysMap.has(date)) {
            daysMap.set(date, new Map());
          }
          const userMap = daysMap.get(date)!;
          // Initialize user entry if not present
          if (!userMap.has(userId)) {
            userMap.set(userId, []);
          }
          // Add the original slot to this date's user entry
          userMap.get(userId)!.push({
            start: startMoment.toISOString(),
            end: endMoment.toISOString(),
          });
          // Move to next day
          current.add(1, 'day');
        }
      }
    }

    // Step 2: Compute availability based on interview type
    const resultSlots: { start: string; end: string }[] = [];
    const sortedDates = Array.from(daysMap.keys()).sort(); // Process dates chronologically

    if (isOneOnOne) {
      // ONE-ON-ONE: Compute union of all slots
      for (const date of sortedDates) {
        const userMap = daysMap.get(date)!;
        // Flatten all slots for this date across users
        const allSlots = Array.from(userMap.values()).flat();
        // Merge overlapping slots for this date
        const merged = mergeOverlappingSlots(allSlots, timezone);
        resultSlots.push(...merged);
      }
      // Merge again across dates to handle adjacent slots (e.g., across midnight)
      output[timeRange] = mergeOverlappingSlots(resultSlots, timezone);
    } else {
      // PANEL: Compute intersection of slots
      for (const date of sortedDates) {
        const userMap = daysMap.get(date)!;
        // Skip if not enough users have availability
        if (userMap.size < requiredUsers) continue;

        // Get slots for each user
        const userSlots = Array.from(userMap.values());
        // Compute intersection for this date
        const commonSlots = computeSlotIntersection(
          userSlots,
          timezone,
          requiredUsers
        );
        resultSlots.push(...commonSlots);
      }
      // Merge adjacent or overlapping slots across dates
      output[timeRange] = mergeOverlappingSlots(resultSlots, timezone);
    }
  }

  return output;
}
